import { Link } from "react-router-dom";
import { ArrowRight, Check, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useInView } from "@/hooks/useInView";
import { STRIPE_PLANS } from "@/lib/stripe-plans";
import { cn } from "@/lib/utils";

const EMPLOYEE_MONTHLY_COST = 4500;
const FABCHAT_MONTHLY_COST = STRIPE_PLANS.inicial.yearly.pricePerMonth;
const MONTHLY_SAVINGS = EMPLOYEE_MONTHLY_COST - FABCHAT_MONTHLY_COST;
const SAVINGS_PERCENT = Math.round((MONTHLY_SAVINGS / EMPLOYEE_MONTHLY_COST) * 100);

const formatBrl = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

type ComparisonRow = {
  label: string;
  employee: string;
  fabchat: string;
  employeeNote?: string;
  fabchatNote?: string;
};

const comparisonRows: ComparisonRow[] = [
  {
    label: "Custo mensal",
    employee: formatBrl(EMPLOYEE_MONTHLY_COST),
    fabchat: formatBrl(FABCHAT_MONTHLY_COST),
    employeeNote: "salário + encargos",
    fabchatNote: "plano Essencial anual",
  },
  {
    label: "Disponibilidade",
    employee: "8h/dia, dias úteis",
    fabchat: "24h, todos os dias",
    employeeNote: "férias e folgas",
    fabchatNote: "sem pausa",
  },
  {
    label: "Conversas simultâneas",
    employee: "~1 por vez",
    fabchat: "Ilimitadas",
    employeeNote: "fila e espera",
    fabchatNote: "WhatsApp e Instagram",
  },
  {
    label: "Canais integrados",
    employee: "Um por pessoa",
    fabchat: "WhatsApp + Instagram",
    employeeNote: "troca de tela",
    fabchatNote: "mesmo painel",
  },
  {
    label: "CRM e histórico",
    employee: "Planilha ou memória",
    fabchat: "Funil, tags e contexto",
    employeeNote: "informação se perde",
    fabchatNote: "passagem para humano",
  },
  {
    label: "Escalar operação",
    employee: "Contratar e treinar",
    fabchat: "Ajustar plano",
    employeeNote: "semanas de onboarding",
    fabchatNote: "minutos",
  },
];

type EnterDelay = "none" | "short" | "medium" | "long";

const enterDelayClass: Record<EnterDelay, string> = {
  none: "",
  short: "motion-safe:delay-100",
  medium: "motion-safe:delay-200",
  long: "motion-safe:delay-300",
};

function enterMotion(isInView: boolean, delay: EnterDelay = "none") {
  return cn(
    "motion-safe:transition-[opacity,transform] motion-safe:duration-700 motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)]",
    enterDelayClass[delay],
    !isInView &&
      "translate-y-3 opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100",
    isInView && "translate-y-0 opacity-100",
  );
}

function CostBar({ isInView }: { isInView: boolean }) {
  const fabchatShare = (FABCHAT_MONTHLY_COST / EMPLOYEE_MONTHLY_COST) * 100;

  return (
    <div className={cn("space-y-4", enterMotion(isInView, "short"))}>
      <div className="space-y-2">
        <div className="flex items-baseline justify-between gap-3 text-sm">
          <span className="font-medium text-foreground">Equipe comercial</span>
          <span className="font-semibold tabular-nums text-foreground">
            {formatBrl(EMPLOYEE_MONTHLY_COST)}
          </span>
        </div>
        <div className="h-3 overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              "h-full rounded-full bg-foreground/35 motion-safe:transition-[width] motion-safe:duration-1000 motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)]",
              isInView ? "w-full" : "w-0 motion-reduce:w-full",
            )}
          />
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-baseline justify-between gap-3 text-sm">
          <span className="font-medium text-primary">FabChat Essencial</span>
          <span className="font-semibold tabular-nums text-primary">
            {formatBrl(FABCHAT_MONTHLY_COST)}
          </span>
        </div>
        <div className="h-3 overflow-hidden rounded-full bg-primary/10">
          <div
            className={cn(
              "h-full rounded-full bg-primary motion-safe:transition-[width] motion-safe:duration-1000 motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)] motion-safe:delay-200",
              !isInView && "w-0",
            )}
            style={{
              width: isInView ? `${Math.max(fabchatShare, 6)}%` : "0%",
            }}
          />
        </div>
      </div>

      <p className="text-sm text-foreground/72">
        Economia de{" "}
        <span className="font-semibold text-foreground">{formatBrl(MONTHLY_SAVINGS)}/mês</span>{" "}
        ({SAVINGS_PERCENT}%) no plano inicial, sem contar férias, turnover ou treinamento.
      </p>
    </div>
  );
}

function ComparisonTable({ isInView }: { isInView: boolean }) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-border/60 bg-card shadow-[0_2px_12px_-2px_hsl(240_10%_15%_/_0.12)]",
        enterMotion(isInView, "medium"),
      )}
    >
      <div className="hidden grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)] border-b border-border/60 bg-muted/40 text-xs font-medium uppercase tracking-wide text-foreground/65 sm:grid">
        <div className="px-4 py-3 sm:px-6">Critério</div>
        <div className="border-l border-border/40 px-4 py-3 sm:px-6">Equipe manual</div>
        <div className="border-l border-border/40 px-4 py-3 text-primary sm:px-6">FabChat</div>
      </div>

      <div className="divide-y divide-border/40">
        {comparisonRows.map((row, index) => (
          <div
            key={row.label}
            className={cn(
              "grid grid-cols-1 sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,1fr)] sm:divide-x sm:divide-border/40",
              "motion-safe:transition-[opacity,transform] motion-safe:duration-700 motion-safe:ease-[cubic-bezier(0.22,1,0.36,1)]",
              !isInView &&
                "translate-y-2 opacity-0 motion-reduce:translate-y-0 motion-reduce:opacity-100",
              isInView && "translate-y-0 opacity-100",
            )}
            style={{ transitionDelay: isInView ? `${120 + index * 60}ms` : undefined }}
          >
            <div className="flex items-start gap-2 px-4 py-4 sm:px-6">
              <span className="text-sm font-semibold text-foreground">{row.label}</span>
            </div>

            <div className="flex items-start gap-2 border-t border-border/40 px-4 py-4 sm:border-t-0 sm:px-6">
              <Minus className="mt-0.5 h-4 w-4 shrink-0 text-foreground/55" aria-hidden />
              <div>
                <p className="text-sm font-medium text-foreground">{row.employee}</p>
                {row.employeeNote && (
                  <p className="mt-0.5 text-xs text-foreground/65">{row.employeeNote}</p>
                )}
              </div>
            </div>

            <div className="flex items-start gap-2 border-t border-border/40 bg-primary/[0.03] px-4 py-4 sm:border-t-0 sm:px-6">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
              <div>
                <p className="text-sm font-semibold text-foreground">{row.fabchat}</p>
                {row.fabchatNote && (
                  <p className="mt-0.5 text-xs text-foreground/65">{row.fabchatNote}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export const CostComparison = () => {
  const { ref, isInView } = useInView({ threshold: 0.12 });

  return (
    <section
      id="cost-comparison"
      className="relative scroll-mt-24 overflow-hidden border-t border-border/40 bg-muted/15 py-20 md:py-28"
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_45%_at_100%_0%,hsl(var(--primary)/0.07),transparent_60%)]"
        aria-hidden
      />

      <div className="container relative mx-auto max-w-6xl px-4">
        <div
          ref={ref}
          className={cn(
            "mb-12 grid gap-10 lg:mb-16 lg:grid-cols-[minmax(0,1fr)_minmax(0,380px)] lg:items-end lg:gap-14",
            enterMotion(isInView),
          )}
        >
          <div>
            <h2 className="text-pretty text-3xl font-bold tracking-tight text-foreground md:text-4xl lg:text-[2.75rem] lg:leading-[1.12]">
              Quanto custa escalar atendimento no{" "}
              <span className="text-primary">WhatsApp</span>?
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-foreground/72 md:text-lg">
              Um atendente comercial custa em média {formatBrl(EMPLOYEE_MONTHLY_COST)} por mês.
              O plano Essencial do FabChat começa em {formatBrl(FABCHAT_MONTHLY_COST)}, com CRM,
              histórico e agentes de IA nos canais onde seu cliente já está.
            </p>
          </div>

          <CostBar isInView={isInView} />
        </div>

        <ComparisonTable isInView={isInView} />

        <div
          className={cn(
            "mt-12 flex flex-col items-start justify-between gap-6 rounded-2xl border border-border/60 bg-card px-6 py-6 sm:flex-row sm:items-center sm:px-8 sm:py-7",
            enterMotion(isInView, "long"),
          )}
        >
          <div className="max-w-xl">
            <p className="text-lg font-semibold tracking-tight text-foreground md:text-xl">
              {formatBrl(MONTHLY_SAVINGS)} a menos por mês. Atendimento que não para quando a equipe
              sai.
            </p>
            <p className="mt-2 text-sm leading-relaxed text-foreground/72 sm:text-base">
              Configure em minutos, conecte WhatsApp e Instagram, e deixe a IA qualificar enquanto sua
              equipe foca no que precisa de humano. Compare os planos e escolha o que cabe na sua
              operação.
            </p>
          </div>

          <Button
            asChild
            className="h-11 shrink-0 rounded-full px-8 text-sm font-semibold shadow-md shadow-primary/25 motion-safe:transition-transform motion-safe:duration-200 motion-safe:hover:scale-[1.02] sm:h-12"
          >
            <Link to="#pricing" className="inline-flex items-center gap-2">
              Ver planos
              <ArrowRight className="h-4 w-4" aria-hidden />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};
